import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Receipt, CreditCard, XCircle, RefreshCw, ExternalLink } from "lucide-react";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
};

const fmtMoney = (n) => {
  if (n === null || n === undefined) return "—";
  return `$${Number(n).toFixed(2)}`;
};

const statusPill = (status) => {
  const base = "px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest";
  switch (status) {
    case "paid": return `${base} bg-emerald-100 text-emerald-800`;
    case "pending": return `${base} bg-amber-100 text-amber-800`;
    case "initiated": return `${base} bg-mm-steel/20 text-mm-navy`;
    case "failed": return `${base} bg-red-100 text-red-800`;
    default: return `${base} bg-mm-navy/10 text-mm-navy`;
  }
};

const Billing = () => {
  const { user, refreshUser, loading } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [acting, setActing] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/account/billing");
      setData(r.data);
    } catch (err) {
      toast.error("Could not load billing");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user?.id]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-mm-navy/60">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;

  const openPortal = async () => {
    setActing(true);
    try {
      const r = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not open billing portal");
      setActing(false);
    }
  };

  const cancelSubscription = async () => {
    setActing(true);
    try {
      const r = await api.post("/account/cancel-subscription");
      toast.success(`Subscription will end on ${formatDate(r.data.access_until)}.`);
      setConfirmCancel(false);
      await load();
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not cancel subscription");
    } finally {
      setActing(false);
    }
  };

  const resumeSubscription = async () => {
    setActing(true);
    try {
      await api.post("/account/resume-subscription");
      toast.success("Subscription resumed");
      await load();
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not resume subscription");
    } finally {
      setActing(false);
    }
  };

  const sub = data?.subscription || {};
  const txns = data?.transactions || [];
  const isActive = sub.subscription_status && ["active", "trialing"].includes(sub.subscription_status);
  const cancelScheduled = !!sub.cancel_at_period_end;

  return (
    <div className="min-h-screen bg-mm-parchment bg-topo">
      <Navbar />
      <div className="container mx-auto py-10 max-w-4xl" data-testid="billing-page">
        <div className="text-xs uppercase tracking-[0.22em] font-bold text-mm-steel">Quartermaster</div>
        <h1 className="font-heading text-4xl font-black text-mm-navy mt-2">Billing</h1>
        <p className="text-mm-navy/70 mt-2">Manage your Summit Pass and see past expedition charges.</p>

        {/* Subscription status card */}
        <div className="mt-8 stage-card p-7" data-testid="billing-subscription-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-mm-gold/20 text-mm-navy flex items-center justify-center">
              <CreditCard size={18} strokeWidth={2.5} />
            </div>
            <h2 className="font-heading text-xl font-bold text-mm-navy">Current plan</h2>
          </div>

          {busy ? (
            <div className="mt-5 text-mm-navy/60">Loading…</div>
          ) : (
            <div className="mt-5 grid sm:grid-cols-2 gap-4">
              <Cell label="Plan" value={
                sub.plan === "premium"
                  ? `Summit Pass · ${sub.premium_plan || "monthly"}`
                  : sub.plan === "trial"
                    ? "Trial · Summit Pass"
                    : "Day Hike (free)"
              } />
              <Cell label="Climbers" value={sub.seats || 1} />
              <Cell label="Status" value={
                <span className={statusPill(sub.subscription_status === "trialing" ? "pending" : "paid")}>
                  {sub.subscription_status || "free"}
                </span>
              } />
              <Cell
                label={cancelScheduled ? "Access until" : "Renews on"}
                value={formatDate(sub.current_period_end || sub.trial_ends_at)}
              />
            </div>
          )}

          {cancelScheduled && (
            <div className="mt-5 rounded-xl bg-amber-50 border-2 border-amber-200 p-4" data-testid="cancel-scheduled-banner">
              <div className="font-bold text-amber-900">Cancellation scheduled</div>
              <p className="text-sm text-amber-900/80 mt-1">
                You'll keep premium access until {formatDate(sub.current_period_end)}. Change your mind anytime.
              </p>
              <button onClick={resumeSubscription} disabled={acting} className="btn-trail btn-gold text-sm mt-3" data-testid="billing-resume-btn">
                <RefreshCw size={14} strokeWidth={2.5} /> {acting ? "Resuming…" : "Resume subscription"}
              </button>
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-3">
            {sub.plan !== "premium" && (
              <Link to="/pricing" className="btn-trail btn-gold text-sm" data-testid="billing-upgrade-btn">
                Upgrade to Summit Pass
              </Link>
            )}
            {sub.stripe_customer_id && (
              <button onClick={openPortal} disabled={acting} className="btn-trail btn-outline-navy text-sm" data-testid="billing-portal-btn">
                <ExternalLink size={14} strokeWidth={2.5} /> {acting ? "Opening…" : "Update card / receipts"}
              </button>
            )}
            {isActive && !cancelScheduled && (
              <button
                onClick={() => setConfirmCancel(true)}
                disabled={acting}
                className="btn-trail text-sm border-red-300 text-red-700 hover:bg-red-50 bg-transparent"
                data-testid="billing-cancel-btn"
              >
                <XCircle size={14} strokeWidth={2.5} /> Cancel subscription
              </button>
            )}
          </div>
        </div>

        {/* Past charges */}
        <div className="mt-8 stage-card p-7" data-testid="billing-history-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-mm-steel/15 text-mm-navy flex items-center justify-center">
              <Receipt size={18} strokeWidth={2.5} />
            </div>
            <h2 className="font-heading text-xl font-bold text-mm-navy">Charge history</h2>
          </div>

          {busy ? (
            <div className="mt-5 text-mm-navy/60">Loading…</div>
          ) : txns.length === 0 ? (
            <div className="mt-5 text-mm-navy/60 py-6 text-center border-2 border-dashed border-mm-navy/10 rounded-xl">
              No charges yet. You'll see receipts here once your first billing cycle begins.
            </div>
          ) : (
            <div className="mt-5 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-mm-steel uppercase text-[11px] tracking-widest font-bold border-b border-mm-navy/10">
                    <th className="py-2 pr-4">Date</th>
                    <th className="py-2 pr-4">Plan</th>
                    <th className="py-2 pr-4">Seats</th>
                    <th className="py-2 pr-4">Amount</th>
                    <th className="py-2 pr-4">Status</th>
                  </tr>
                </thead>
                <tbody className="text-mm-navy">
                  {txns.map((t) => (
                    <tr key={t.id || t.session_id} className="border-b border-mm-navy/5" data-testid={`txn-${t.id}`}>
                      <td className="py-2.5 pr-4">{formatDate(t.created_at)}</td>
                      <td className="py-2.5 pr-4 capitalize">{t.plan || "—"}</td>
                      <td className="py-2.5 pr-4">{t.seats || 1}</td>
                      <td className="py-2.5 pr-4 font-mono">{fmtMoney(t.amount)}</td>
                      <td className="py-2.5 pr-4">
                        <span className={statusPill(t.payment_status)}>{t.payment_status || t.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Cancel confirmation */}
        {confirmCancel && (
          <div className="fixed inset-0 bg-mm-navy/60 backdrop-blur-sm z-50 flex items-center justify-center px-4" data-testid="billing-cancel-modal">
            <div className="bg-white rounded-2xl p-7 max-w-md w-full border-2 border-red-200">
              <div className="text-xs uppercase tracking-widest font-bold text-red-700">Are you sure?</div>
              <h3 className="font-heading text-xl font-black text-mm-navy mt-1">Cancel Summit Pass?</h3>
              <p className="text-sm text-mm-navy/70 mt-2">
                You'll keep full premium access until {formatDate(sub.current_period_end || sub.trial_ends_at)}.
                After that, your account moves to the free Day Hike plan. Your climbers, badges, and expeditions stay saved.
              </p>
              <div className="mt-5 flex justify-end gap-2">
                <button onClick={() => setConfirmCancel(false)} disabled={acting} className="btn-trail btn-outline-navy text-sm">
                  Keep climbing
                </button>
                <button
                  onClick={cancelSubscription}
                  disabled={acting}
                  className="btn-trail text-sm bg-red-600 hover:bg-red-700 text-white border-red-600"
                  data-testid="billing-cancel-confirm-btn"
                >
                  {acting ? "Canceling…" : "Yes, cancel"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Cell = ({ label, value }) => (
  <div>
    <div className="text-xs uppercase tracking-widest font-bold text-mm-steel">{label}</div>
    <div className="text-mm-navy font-heading font-bold mt-1">{value}</div>
  </div>
);

export default Billing;
